import type React from 'react';
import { DEFAULT_BRANDING_COMPANY_NAME } from '@config/config';
import LocalStorageKey from '@enums/LocalStorageKey';
import LocalStorageService from '@services/LocalStorageService';
import { getCompanyFromUrl } from '@utils/index';
import { create } from 'zustand';
import { createJSONStorage, persist, subscribeWithSelector } from 'zustand/middleware';
import { SpendWindow, AdFiltersState } from '@customTypes/ad/AdFilters';
import { getAppStorePersistKey } from '@stores/utils';

const DEMO_STORAGE_KEY = 'leonardo_demo_mode';

function initDemoMode(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get('demo') === 'true') {
    sessionStorage.setItem(DEMO_STORAGE_KEY, 'true');
    return true;
  }
  return sessionStorage.getItem(DEMO_STORAGE_KEY) === 'true';
}

export type TabEntityType = 'campaign' | 'adset' | 'ad' | 'report' | 'asset' | 'scheduledWork' | 'scheduledWorkExecution';

/** Sort key for ads within an adset (app-wide, synced across tabs). */
export type AdSortKey =
  | 'name-asc'
  | 'name-desc'
  | 'status'
  | 'spend-desc'
  | 'spend-asc'
  | 'date-desc'
  | 'date-asc';

/** Sort key for adsets within a campaign (app-wide, synced across tabs). */
export type AdsetSortKey = AdSortKey;

export interface DrillDown {
  entityType: 'adset' | 'ad';
  entityId: string;
  entityName: string;
  parentAdsetId?: string;
  parentAdsetName?: string;
}

const AD_PLATFORMS = new Set(['meta', 'google', 'linkedin', 'reddit', 'tiktok', 'bing']);

export interface TabPane {
  label: string;
  key: string;
  entityType: TabEntityType;
  entityId: string;
  entityName: string;
  // Per-tab ad sorting preference (defaults to 'name-asc' if not set)
  adSort?: AdSortKey;
  // Additional metadata that might be useful
  metadata?: {
    status?: string;
    roas?: number | null;
    spend?: number | null;
    drillDown?: DrillDown | null;
    [key: string]: any;
  };
}

type AppActions = {
  setSidebarCollapsed: (collapsed: boolean) => void;
  setPageTitle: (pageTitle: string) => void;
  setCompanyName: (companyName: string) => void;
  getCompanyName: () => string;
  setIsConnectionLoading: (isConnectionLoading: boolean) => void;
  setSurveyValues: (surveyValues: any) => void;
  setIsLeonardoOpen: (isLeonardoOpen: boolean) => void;
  setIsLeonardoNewOpen: (isLeonardoNewOpen: boolean) => void;
  setLeonardoInitialMessage: (message: string | null) => void;
  openLeonardoWithMessage: (message: string) => void;
  setMockChatMode: (enabled: boolean) => void;
  addTab: (tab: TabPane) => void;
  updateTab: (key: string, updates: Partial<Pick<TabPane, 'label' | 'entityName' | 'metadata' | 'adSort'>>) => void;
  removeTab: (key: string) => void;
  drillDownInTab: (tabKey: string, drillDown: DrillDown | null) => void;
  navigateToEntity: (opts: {
    entityType: TabEntityType;
    entityId: string;
    entityName: string;
    platform: string;
    parentCampaignId?: string;
    parentCampaignName?: string;
    parentAdsetId?: string;
    parentAdsetName?: string;
    metadata?: Record<string, any>;
  }) => void;
  clearAllTabs: () => void;
  resetStore: () => void;
  goHome: () => void;
  setActiveTab: (key: string) => void;
  setSelectedTab: (key: string | null) => void;
  setAdsTreeExpandedKeys: (keys: React.Key[]) => void;
  setReportsTreeExpandedKeys: (keys: React.Key[]) => void;
  setAssetTreeExpandedKeys: (keys: React.Key[]) => void;
  setScheduledWorkTreeExpandedKeys: (keys: React.Key[]) => void;
  collapseAllSidebarTrees: () => void;
  expandSidebarTree: (tree: 'ads' | 'reports' | 'assets' | 'scheduledWork', keys: React.Key[]) => void;
  setMetricsWindow: (window: SpendWindow) => void;
  setSidebarAdFilters: (filters: AdFiltersState | ((prev: AdFiltersState) => AdFiltersState)) => void;
  setWelcomeFlowPending: (pending: boolean) => void;
  setWelcomeMessageSentAt: (timestamp: number | null) => void;
  setAdSort: (key: AdSortKey) => void;
  setAdsetSort: (key: AdsetSortKey) => void;
  setSidebarPanelWidth: (width: number) => void;
  setChatbotPanelWidth: (width: number) => void;
  setDemoMode: (enabled: boolean) => void;
  revealInSidebar: (reveal: SidebarReveal) => void;
  clearSidebarReveal: () => void;
  setNudgesInitializing: (initializing: boolean) => void;
};

export interface SidebarReveal {
  entityType: TabEntityType;
  entityId: string;
  platform?: string;
  tabKey?: string;
}

interface AppState {
  isSidebarCollapsed: boolean;
  pageTitle: string;
  loading: boolean;
  error: string | null;
  companyName: string;
  isDefaultCompany: boolean;
  isConnectionLoading: boolean;
  isLeonardoOpen: boolean;
  isLeonardoNewOpen: boolean;
  leonardoInitialMessage: string | null;
  mockChatMode: boolean;
  tabs: TabPane[];
  activeTabKey: string | null;
  selectedTab: string | null;
  adsTreeExpandedKeys: React.Key[];
  reportsTreeExpandedKeys: React.Key[];
  assetTreeExpandedKeys: React.Key[];
  scheduledWorkTreeExpandedKeys: React.Key[];
  metricsWindow: SpendWindow;
  // Tracks if the post-onboarding welcome flow is pending (survives refresh, per-workspace)
  welcomeFlowPending: boolean;
  // Timestamp when the welcome message was sent (persisted to prevent duplicate sends on refresh)
  welcomeMessageSentAt: number | null;
  adSort: AdSortKey;
  adsetSort: AdsetSortKey;
  sidebarPanelWidth: number;
  chatbotPanelWidth: number;
  sidebarAdFilters: AdFiltersState;
  demoMode: boolean;
  pendingSidebarReveal: SidebarReveal | null;
  nudgesInitializing: boolean;
}

export type AppStore = AppState & AppActions;

const STORE_NAME = 'app';
export const DEFAULT_SIDEBAR_PANEL_WIDTH = 320;
export const DEFAULT_CHATBOT_PANEL_WIDTH = 450;
export const MIN_CHATBOT_PANEL_WIDTH = 300;

const normalizeChatbotPanelWidth = (width: number): number => {
  if (!Number.isFinite(width)) {
    return DEFAULT_CHATBOT_PANEL_WIDTH;
  }
  return Math.max(MIN_CHATBOT_PANEL_WIDTH, Math.round(width));
};

const initialState: AppState = {
  isSidebarCollapsed: false, // Default to open on larger screens
  pageTitle: '',
  loading: false,
  error: null,
  companyName: '',
  isDefaultCompany: false,
  isConnectionLoading: false,
  isLeonardoOpen: false,
  isLeonardoNewOpen: false,
  leonardoInitialMessage: null,
  mockChatMode: true, // Set to true for testing
  tabs: [],
  activeTabKey: null,
  selectedTab: null,
  adsTreeExpandedKeys: [],
  reportsTreeExpandedKeys: [],
  assetTreeExpandedKeys: [],
  scheduledWorkTreeExpandedKeys: [],
  metricsWindow: { type: '7d' },
  welcomeFlowPending: false,
  welcomeMessageSentAt: null,
  adSort: 'name-asc',
  adsetSort: 'name-asc',
  sidebarPanelWidth: DEFAULT_SIDEBAR_PANEL_WIDTH,
  chatbotPanelWidth: DEFAULT_CHATBOT_PANEL_WIDTH,
  sidebarAdFilters: {
    statusFilter: 'all',
    mediaTypeFilter: 'all',
    sortOrder: 'newest',
    spendWindow: { type: '7d' },
  },
  demoMode: initDemoMode() || import.meta.env.VITE_DEMO_MODE === 'true',
  pendingSidebarReveal: null,
  nudgesInitializing: true,
};

const appStoreStorage = createJSONStorage<Pick<
  AppState,
  | 'isSidebarCollapsed'
  | 'tabs'
  | 'activeTabKey'
  | 'adsTreeExpandedKeys'
  | 'reportsTreeExpandedKeys'
  | 'assetTreeExpandedKeys'
  | 'scheduledWorkTreeExpandedKeys'
  | 'metricsWindow'
  | 'welcomeFlowPending'
  | 'welcomeMessageSentAt'
  | 'adSort'
  | 'adsetSort'
  | 'sidebarPanelWidth'
  | 'chatbotPanelWidth'
  | 'sidebarAdFilters'
>>(() => ({
  getItem: (_name: string) => localStorage.getItem(getAppStorePersistKey(STORE_NAME)),
  setItem: (_name: string, value: string) => localStorage.setItem(getAppStorePersistKey(STORE_NAME), value),
  removeItem: (_name: string) => localStorage.removeItem(getAppStorePersistKey(STORE_NAME)),
}));

export const useAppStore = create<AppStore>()(
  persist(
    subscribeWithSelector((set, get) => ({
      ...initialState,

      setIsConnectionLoading: (isConnectionLoading) =>
        set({ isConnectionLoading }),
      setSidebarCollapsed: (collapsed) => set({ isSidebarCollapsed: collapsed }),
      setPageTitle: (pageTitle: string) => set({ pageTitle }),
      setIsLeonardoOpen: (isLeonardoOpen) => set({ isLeonardoOpen }),
      setIsLeonardoNewOpen: (isLeonardoNewOpen) => set({ isLeonardoNewOpen }),
      setLeonardoInitialMessage: (message) => set({ leonardoInitialMessage: message }),
      openLeonardoWithMessage: (message) => {
        // Use New Leonardo as the primary system
        // For now, just open New Leonardo (initial message support can be added later)
        set({ isLeonardoNewOpen: true, leonardoInitialMessage: message });
      },
      setMockChatMode: (enabled) => set({ mockChatMode: enabled }),
      setCompanyName: (companyName: string) => set({ companyName }),
      getCompanyName: () => {
        const defaultCompanyFor = ['localhost', '127.0.0.1'];
        const { companyName } = get();
        const companyNameFromUrl = getCompanyFromUrl();
        let currentCompanyName = '';

        if (defaultCompanyFor.includes(companyNameFromUrl)) {
          currentCompanyName = companyName || DEFAULT_BRANDING_COMPANY_NAME;
        } else if (
          companyNameFromUrl &&
          companyNameFromUrl !== DEFAULT_BRANDING_COMPANY_NAME
        ) {
          currentCompanyName = companyNameFromUrl;
        } else if (
          companyName &&
          companyNameFromUrl === DEFAULT_BRANDING_COMPANY_NAME
        ) {
          currentCompanyName = companyName;
        } else {
          currentCompanyName = DEFAULT_BRANDING_COMPANY_NAME;
        }

        // Ensure we never return empty string
        const finalCompanyName = currentCompanyName || DEFAULT_BRANDING_COMPANY_NAME;

        return finalCompanyName;
      },
      getSurveyValues: () => {
        try {
          const surveyValues = LocalStorageService.getItem(
            LocalStorageKey.SURVEY_FORM_VALUES,
          ) as string;
          return surveyValues ? JSON.parse(surveyValues) : null;
        } catch (error) {
          console.error('Error getting survey values', error);
          return null;
        }
      },
      setSurveyValues: (surveyValues: any) => {
        LocalStorageService.setItem(
          LocalStorageKey.SURVEY_FORM_VALUES,
          JSON.stringify(surveyValues),
        );
      },
      addTab: (tab: TabPane) => {
        const { tabs } = get();
        const existingTab = tabs.find((t) => t.key === tab.key);
        if (existingTab) {
          // When re-selecting a campaign tab directly, clear any drill-down
          if (existingTab.entityType === 'campaign' && existingTab.metadata?.drillDown) {
            const updatedTabs = tabs.map((t) =>
              t.key === tab.key ? { ...t, metadata: { ...t.metadata, drillDown: null } } : t
            );
            set({ tabs: updatedTabs, activeTabKey: tab.key });
          } else {
            set({ activeTabKey: tab.key });
          }
        } else {
          set({ tabs: [...tabs, tab], activeTabKey: tab.key });
        }
      },
      drillDownInTab: (tabKey: string, drillDown: DrillDown | null) => {
        const { tabs } = get();
        const updatedTabs = tabs.map((tab) => {
          if (tab.key === tabKey) {
            return { ...tab, metadata: { ...tab.metadata, drillDown } };
          }
          return tab;
        });
        set({ tabs: updatedTabs, activeTabKey: tabKey });
      },
      navigateToEntity: (opts) => {
        const { entityType, entityId, entityName, platform, parentCampaignId, parentCampaignName, parentAdsetId, parentAdsetName, metadata } = opts;

        // Campaign-level: open as a regular tab
        if (entityType === 'campaign' || !AD_PLATFORMS.has(platform)) {
          get().addTab({ key: `${entityType}-${entityId}`, label: entityName, entityType, entityId, entityName, metadata: { ...metadata, platform } });
          return;
        }

        // Adset or ad: open the parent campaign tab with drill-down in a single set()
        if (parentCampaignId && parentCampaignName) {
          const campaignTabKey = `campaign-${parentCampaignId}`;
          const drillDown: DrillDown = { entityType: entityType as 'adset' | 'ad', entityId, entityName };
          if (entityType === 'ad' && parentAdsetId) {
            drillDown.parentAdsetId = parentAdsetId;
            drillDown.parentAdsetName = parentAdsetName;
          }

          const { tabs } = get();
          const existingTab = tabs.find((t) => t.key === campaignTabKey);
          if (existingTab) {
            const updatedTabs = tabs.map((t) =>
              t.key === campaignTabKey ? { ...t, metadata: { ...t.metadata, drillDown } } : t
            );
            set({ tabs: updatedTabs, activeTabKey: campaignTabKey });
          } else {
            const newTab: TabPane = { key: campaignTabKey, label: parentCampaignName, entityType: 'campaign', entityId: parentCampaignId, entityName: parentCampaignName, metadata: { ...metadata, platform, drillDown } };
            set({ tabs: [...tabs, newTab], activeTabKey: campaignTabKey });
          }
        } else {
          // Fallback: no parent info available, open as standalone tab
          get().addTab({ key: `${entityType}-${entityId}`, label: entityName, entityType, entityId, entityName, metadata: { ...metadata, platform } });
        }
      },
      updateTab: (key: string, updates: Partial<Pick<TabPane, 'label' | 'entityName' | 'metadata' | 'adSort'>>) => {
        const { tabs } = get();
        const updatedTabs = tabs.map((tab) => {
          if (tab.key === key) {
            return {
              ...tab,
              ...updates,
              metadata: updates.metadata ? { ...tab.metadata, ...updates.metadata } : tab.metadata,
            };
          }
          return tab;
        });
        set({ tabs: updatedTabs });
      },
      removeTab: (key: string) => {
        const { tabs, activeTabKey } = get();
        const targetIndex = tabs.findIndex((pane) => pane.key === key);
        if (targetIndex === -1) return;

        const newPanes = tabs.filter((pane) => pane.key !== key);

        let newActiveKey: string | null = activeTabKey;
        if (key === activeTabKey) {
          if (newPanes.length > 0) {
            // Switch to adjacent tab
            const nextIndex = targetIndex >= newPanes.length ? newPanes.length - 1 : targetIndex;
            newActiveKey = newPanes[nextIndex].key;
          } else {
            // No tabs left -- go home
            newActiveKey = null;
          }
        }

        // Safety: if no tabs remain, always go home regardless of prior activeTabKey.
        // Guards against edge cases where onChange fires during removal and sets a stale key.
        if (newPanes.length === 0) {
          newActiveKey = null;
        }

        set({ tabs: newPanes, activeTabKey: newActiveKey });
      },
      clearAllTabs: () => {
        set({ tabs: [], activeTabKey: null });
      },
      resetStore: () => {
        set({
          ...initialState,
          // Preserve responsive sidebar state based on screen size
          isSidebarCollapsed: typeof window !== 'undefined' && window.innerWidth < 640 ? true : initialState.isSidebarCollapsed,
        });
      },
      goHome: () => {
        set({ activeTabKey: null });
      },
      setActiveTab: (key: string) => {
        set({ activeTabKey: key });
      },
      setSelectedTab: (key: string | null) => {
        set({ selectedTab: key });
      },
      setAdsTreeExpandedKeys: (keys: React.Key[]) => {
        set({ adsTreeExpandedKeys: keys });
      },
      setReportsTreeExpandedKeys: (keys: React.Key[]) => {
        set({ reportsTreeExpandedKeys: keys });
      },
      setAssetTreeExpandedKeys: (keys: React.Key[]) => {
        set({ assetTreeExpandedKeys: keys });
      },
      setScheduledWorkTreeExpandedKeys: (keys: React.Key[]) => {
        set({ scheduledWorkTreeExpandedKeys: keys });
      },
      collapseAllSidebarTrees: () => {
        set({
          adsTreeExpandedKeys: [],
          reportsTreeExpandedKeys: [],
          assetTreeExpandedKeys: [],
          scheduledWorkTreeExpandedKeys: [],
        });
      },
      expandSidebarTree: (tree: 'ads' | 'reports' | 'assets' | 'scheduledWork', keys: React.Key[]) => {
        const state = get();
        const merge = (existing: React.Key[]) => Array.from(new Set([...existing, ...keys]));
        // Ads: only one platform accordion (platform-*) open at a time; merge() alone would keep every platform key ever opened.
        const mergeAds = (existing: React.Key[]) => {
          const addsPlatformRoot = keys.some((k) => String(k).startsWith('platform-'));
          if (!addsPlatformRoot) {
            return merge(existing);
          }
          const withoutPlatformRoots = existing.filter((k) => !String(k).startsWith('platform-'));
          return Array.from(new Set([...withoutPlatformRoots, ...keys]));
        };
        // Remove root keys from other trees so they collapse, but keep subtree keys for when they reopen
        const collapseAds = state.adsTreeExpandedKeys.filter((k) => !String(k).startsWith('platform-'));
        const collapseOther = (existing: React.Key[]) => existing.filter((k) => k !== '0-0-0');
        set({
          adsTreeExpandedKeys: tree === 'ads' ? mergeAds(state.adsTreeExpandedKeys) : collapseAds,
          reportsTreeExpandedKeys: tree === 'reports' ? merge(state.reportsTreeExpandedKeys) : collapseOther(state.reportsTreeExpandedKeys),
          assetTreeExpandedKeys: tree === 'assets' ? merge(state.assetTreeExpandedKeys) : collapseOther(state.assetTreeExpandedKeys),
          scheduledWorkTreeExpandedKeys: tree === 'scheduledWork' ? merge(state.scheduledWorkTreeExpandedKeys) : collapseOther(state.scheduledWorkTreeExpandedKeys),
        });
      },
      setMetricsWindow: (window: SpendWindow) => {
        set({ metricsWindow: window });
      },
      setSidebarAdFilters: (filters: AdFiltersState | ((prev: AdFiltersState) => AdFiltersState)) => {
        set((state) => ({
          sidebarAdFilters:
            typeof filters === 'function' ? filters(state.sidebarAdFilters) : filters,
        }));
      },
      setWelcomeFlowPending: (pending: boolean) => {
        set({ welcomeFlowPending: pending });
      },
      setWelcomeMessageSentAt: (timestamp: number | null) => {
        set({ welcomeMessageSentAt: timestamp });
      },
      setAdSort: (adSort: AdSortKey) => set({ adSort }),
      setAdsetSort: (adsetSort: AdsetSortKey) => set({ adsetSort }),
      setSidebarPanelWidth: (sidebarPanelWidth: number) => {
        const rounded = Math.round(sidebarPanelWidth);
        if (get().sidebarPanelWidth === rounded) return;
        set({ sidebarPanelWidth: rounded });
      },
      setChatbotPanelWidth: (chatbotPanelWidth: number) => {
        const normalizedWidth = normalizeChatbotPanelWidth(chatbotPanelWidth);
        if (get().chatbotPanelWidth === normalizedWidth) {
          return;
        }
        set({ chatbotPanelWidth: normalizedWidth });
      },
      setDemoMode: (enabled: boolean) => {
        if (enabled) {
          sessionStorage.setItem(DEMO_STORAGE_KEY, 'true');
        } else {
          sessionStorage.removeItem(DEMO_STORAGE_KEY);
        }
        set({ demoMode: enabled });
      },
      revealInSidebar: (reveal: SidebarReveal) => {
        set({ pendingSidebarReveal: reveal });
      },
      clearSidebarReveal: () => {
        set({ pendingSidebarReveal: null });
      },
      setNudgesInitializing: (initializing: boolean) => {
        set({ nudgesInitializing: initializing });
      },
    })),
    {
      name: STORE_NAME,
      storage: appStoreStorage,
      version: 1,
      migrate: (persistedState: any, version: number) => {
        if (version < 1) {
          // Drop ad-platform adset/ad tabs — they're now drill-downs inside campaign tabs
          const tabs = (persistedState.tabs || []).filter((tab: any) => {
            if ((tab.entityType === 'adset' || tab.entityType === 'ad') && tab.metadata?.platform && AD_PLATFORMS.has(tab.metadata.platform)) {
              return false;
            }
            return true;
          });
          // If the active tab was removed, reset to home
          const activeTabKey = tabs.some((t: any) => t.key === persistedState.activeTabKey)
            ? persistedState.activeTabKey
            : null;
          return { ...persistedState, tabs, activeTabKey };
        }
        return persistedState;
      },
      partialize: (state) => ({
        isSidebarCollapsed: state.isSidebarCollapsed,
        tabs: state.tabs,
        activeTabKey: state.activeTabKey,
        adsTreeExpandedKeys: state.adsTreeExpandedKeys,
        reportsTreeExpandedKeys: state.reportsTreeExpandedKeys,
        assetTreeExpandedKeys: state.assetTreeExpandedKeys,
        scheduledWorkTreeExpandedKeys: state.scheduledWorkTreeExpandedKeys,
        metricsWindow: state.metricsWindow,
        welcomeFlowPending: state.welcomeFlowPending,
        welcomeMessageSentAt: state.welcomeMessageSentAt,
        adSort: state.adSort,
        adsetSort: state.adsetSort,
        sidebarPanelWidth: state.sidebarPanelWidth,
        chatbotPanelWidth: state.chatbotPanelWidth,
        sidebarAdFilters: state.sidebarAdFilters,
      }),
    },
  ),
);

// Selectors for optimized subscriptions (implementations must be named hooks for eslint-plugin-react-hooks)
function useAppSelectorLoading() {
  return useAppStore((state) => state.loading);
}
function useAppSelectorError() {
  return useAppStore((state) => state.error);
}
function useAppSelectorIsSidebarCollapsed() {
  return useAppStore((state) => state.isSidebarCollapsed);
}
function useAppSelectorPageTitle() {
  return useAppStore((state) => state.pageTitle);
}
function useAppSelectorIsConnectionLoading() {
  return useAppStore((state) => state.isConnectionLoading);
}
function useAppSelectorIsLeonardoOpen() {
  return useAppStore((state) => state.isLeonardoOpen);
}
function useAppSelectorIsLeonardoNewOpen() {
  return useAppStore((state) => state.isLeonardoNewOpen);
}
function useAppSelectorLeonardoInitialMessage() {
  return useAppStore((state) => state.leonardoInitialMessage);
}
function useAppSelectorMockChatMode() {
  return useAppStore((state) => state.mockChatMode);
}
function useAppSelectorTabs() {
  return useAppStore((state) => state.tabs);
}
function useAppSelectorActiveTabKey() {
  return useAppStore((state) => state.activeTabKey);
}
function useAppSelectorAdsTreeExpandedKeys() {
  return useAppStore((state) => state.adsTreeExpandedKeys);
}
function useAppSelectorReportsTreeExpandedKeys() {
  return useAppStore((state) => state.reportsTreeExpandedKeys);
}
function useAppSelectorAssetTreeExpandedKeys() {
  return useAppStore((state) => state.assetTreeExpandedKeys);
}
function useAppSelectorScheduledWorkTreeExpandedKeys() {
  return useAppStore((state) => state.scheduledWorkTreeExpandedKeys);
}
function useAppSelectorMetricsWindow() {
  return useAppStore((state) => state.metricsWindow);
}
function useAppSelectorWelcomeFlowPending() {
  return useAppStore((state) => state.welcomeFlowPending);
}
function useAppSelectorWelcomeMessageSentAt() {
  return useAppStore((state) => state.welcomeMessageSentAt);
}
function useAppSelectorAdSort() {
  return useAppStore((state) => state.adSort);
}
function useAppSelectorAdsetSort() {
  return useAppStore((state) => state.adsetSort);
}
function useAppSelectorChatbotPanelWidth() {
  return useAppStore((state) => state.chatbotPanelWidth);
}
function useAppSelectorSidebarAdFilters() {
  return useAppStore((state) => state.sidebarAdFilters);
}
function useAppSelectorDemoMode() {
  return useAppStore((state) => state.demoMode);
}
function useAppSelectorPendingSidebarReveal() {
  return useAppStore((state) => state.pendingSidebarReveal);
}
function useAppSelectorNudgesInitializing() {
  return useAppStore((state) => state.nudgesInitializing);
}

export const useAppSelectors = {
  loading: useAppSelectorLoading,
  error: useAppSelectorError,
  isSidebarCollapsed: useAppSelectorIsSidebarCollapsed,
  pageTitle: useAppSelectorPageTitle,
  isConnectionLoading: useAppSelectorIsConnectionLoading,
  isLeonardoOpen: useAppSelectorIsLeonardoOpen,
  isLeonardoNewOpen: useAppSelectorIsLeonardoNewOpen,
  leonardoInitialMessage: useAppSelectorLeonardoInitialMessage,
  mockChatMode: useAppSelectorMockChatMode,
  tabs: useAppSelectorTabs,
  activeTabKey: useAppSelectorActiveTabKey,
  adsTreeExpandedKeys: useAppSelectorAdsTreeExpandedKeys,
  reportsTreeExpandedKeys: useAppSelectorReportsTreeExpandedKeys,
  assetTreeExpandedKeys: useAppSelectorAssetTreeExpandedKeys,
  scheduledWorkTreeExpandedKeys: useAppSelectorScheduledWorkTreeExpandedKeys,
  metricsWindow: useAppSelectorMetricsWindow,
  welcomeFlowPending: useAppSelectorWelcomeFlowPending,
  welcomeMessageSentAt: useAppSelectorWelcomeMessageSentAt,
  adSort: useAppSelectorAdSort,
  adsetSort: useAppSelectorAdsetSort,
  chatbotPanelWidth: useAppSelectorChatbotPanelWidth,
  sidebarAdFilters: useAppSelectorSidebarAdFilters,
  demoMode: useAppSelectorDemoMode,
  pendingSidebarReveal: useAppSelectorPendingSidebarReveal,
  nudgesInitializing: useAppSelectorNudgesInitializing,
};
